// Control de caja del admin (backend docs/spec-control-de-caja.md).
//
// Lógica pura de la pantalla: rótulos, filtros y totales sobre lo que devuelve
// `GET cashSession/:id/control`. Vive acá y no en el componente para poder testearla sin montar
// nada: lo que importa es que los totales que se muestran sumen lo mismo que los baldes del
// backend, y eso se rompe en silencio si un filtro se olvida de los anulados.

// Medio de pago, con el nombre que se usa en el mostrador ("Tarjeta", no "POS": en este proyecto
// "POS" significa tres cosas distintas).
export const MEDIOS = {
    1: 'Efectivo',
    2: 'Transferencia',
    3: 'Mercado Pago',
    4: 'Mercado Libre',
    5: 'Tarjeta',
    6: 'Cuenta del equipo',
    7: 'Incluido en evento',
    8: 'Premio o cortesía',
};

// Los que mueven plata. El resto son tickets que el arqueo no reclama (consumo a cuenta, lo ya
// pagado con la entrada de un evento, un voucher) y se informan aparte.
export const MEDIOS_PLATA = [1, 2, 3, 5];

export const ORIGENES = {
    tienda: 'Tienda',
    cafeteria: 'Cafetería',
    cafeteria_mesa: 'Cafetería · mesa',
    mesa: 'Mesa',
    evento: 'Evento',
    membresia: 'Membresía',
    gift_card: 'Gift card',
    web: 'Web',
};

export const FACTURA = { 0: 'Pendiente', 1: 'Facturada', 2: 'No se factura' };

export const MOVIMIENTOS = {
    RETIRO: 'Retiro',
    GASTO: 'Gasto pagado del cajón',
    INGRESO: 'Ingreso',
    AJUSTE_FONDO: 'Ajuste de fondo',
};
export const MOVIMIENTOS_SALIDA = ['RETIRO', 'GASTO'];

export const CUENTA_EQUIPO = {
    cargo: 'Consumo a cuenta',
    pago: 'Pago',
    reversa: 'Reversa de consumo',
    ajuste: 'Ajuste',
};

export const pesos = (n) => {
    if (n === null || n === undefined || n === '') return '—';
    const valor = Math.round(Number(n) * 100) / 100;
    const signo = valor < 0 ? '−' : '';
    return `${signo}$${Math.abs(valor).toLocaleString('de-DE', { maximumFractionDigits: 2 })}`;
};

// Una diferencia: con signo explícito, porque "+$200" y "−$200" son cosas opuestas (sobra / falta).
export const diferencia = (n) => {
    if (n === null || n === undefined) return '—';
    if (Math.abs(Number(n)) < 1) return '$0';
    return Number(n) > 0 ? `+${pesos(n)}` : pesos(n);
};

export const hayDiferencia = (n) => n !== null && n !== undefined && Math.abs(Number(n)) >= 1;

// El backend manda todas las horas como literal de pared 'YYYY-MM-DD HH:mm:ss'. NO se pasan por
// `new Date()`: el navegador las tomaría como hora local o UTC según el formato, y es justo el bug
// de las tres horas que ya se pagó una vez.
export const hora = (pared) => (pared ? String(pared).slice(11, 16) : '—');

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

export const fecha = (pared) => {
    if (!pared) return '—';
    const [y, m, d] = String(pared).slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
};

export const fechaConDia = (pared) => {
    if (!pared) return '—';
    const [y, m, d] = String(pared).slice(0, 10).split('-').map(Number);
    // Date.UTC + getUTCDay: el día de la semana de una fecha de calendario, sin zona de por medio.
    const dia = DIAS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
    return `${dia} ${fecha(pared)}`;
};

// Cuánto estuvo abierta, en "12 h 05 min".
export const duracion = (desde, hasta) => {
    if (!desde || !hasta) return '—';
    const ms = (w) => Date.parse(`${String(w).replace(' ', 'T')}Z`);
    const minutos = Math.max(0, Math.round((ms(hasta) - ms(desde)) / 60000));
    const h = Math.floor(minutos / 60);
    const min = String(minutos % 60).padStart(2, '0');
    return h > 0 ? `${h} h ${min} min` : `${minutos} min`;
};

export function estadoTicket(t) {
    if (t.cancelled) return 'Anulada';
    if (t.pending) return 'Pago pendiente';
    if (!t.counted) return 'No entra al arqueo';
    return 'Cobrada';
}

/**
 * Filtra la lista de ventas. Cada filtro vacío no filtra. `ids` gana sobre todo lo demás: es el
 * "ver estas" de una alerta, y combinarlo con otro filtro escondería justo lo que se fue a mirar.
 */
export function filtrarTickets(tickets, { medio = '', origen = '', lector = '', estado = '', texto = '', ids = null } = {}) {
    if (ids && ids.length > 0) {
        const set = new Set(ids.map(Number));
        return tickets.filter((t) => set.has(Number(t.ticketId)));
    }
    const q = String(texto || '').trim().toLowerCase();
    return tickets.filter((t) => {
        if (medio !== '' && String(t.paymentType) !== String(medio)) return false;
        if (origen !== '' && t.origin !== origen) return false;
        if (lector === 'sin' && !(t.paymentType === 5 && t.pos_device_id == null)) return false;
        if (lector !== '' && lector !== 'sin' && String(t.pos_device_id) !== String(lector)) return false;
        if (estado === 'cobradas' && !t.counted) return false;
        if (estado === 'anuladas' && !t.cancelled) return false;
        if (estado === 'pendientes' && !(t.pending && !t.cancelled)) return false;
        if (estado === 'cambiadas' && !(t.changes && t.changes.length > 0)) return false;
        if (estado === 'sin_facturar' && !(t.counted && t.total > 0 && t.invoiceStatus === 0 && MEDIOS_PLATA.includes(t.paymentType))) return false;
        if (q) {
            const enLineas = (t.lines || []).some((l) => String(l.name || '').toLowerCase().includes(q));
            const campos = [t.ticketId, t.purchaseId, t.customer, t.cedula, t.discount_code]
                .filter((x) => x !== null && x !== undefined)
                .map((x) => String(x).toLowerCase());
            if (!enLineas && !campos.some((c) => c.includes(q))) return false;
        }
        return true;
    });
}

/** Lo que suma una lista: sólo lo que entra al arqueo (un anulado o un pendiente se ven, pero no suman). */
export function totalCobrado(tickets) {
    return tickets.filter((t) => t.counted && MEDIOS_PLATA.includes(t.paymentType))
        .reduce((total, t) => total + Number(t.total || 0), 0);
}

/**
 * Ventas por origen × medio, sólo lo cobrado. Las columnas son los medios que aparecen, en el
 * orden del mostrador; las filas, los orígenes con algo cobrado.
 */
export function porOrigenYMedio(tickets) {
    const cobradas = tickets.filter((t) => t.counted && MEDIOS_PLATA.includes(t.paymentType));
    const medios = MEDIOS_PLATA.filter((m) => cobradas.some((t) => t.paymentType === m));
    const filas = {};
    cobradas.forEach((t) => {
        const fila = filas[t.origin] || (filas[t.origin] = { origin: t.origin, label: ORIGENES[t.origin] || t.origin, porMedio: {}, total: 0, cantidad: 0 });
        fila.porMedio[t.paymentType] = (fila.porMedio[t.paymentType] || 0) + Number(t.total || 0);
        fila.total += Number(t.total || 0);
        fila.cantidad += 1;
    });
    const orden = Object.keys(ORIGENES);
    const rows = Object.values(filas).sort((a, b) => orden.indexOf(a.origin) - orden.indexOf(b.origin));
    const totales = medios.reduce((acc, m) => {
        acc[m] = rows.reduce((total, r) => total + (r.porMedio[m] || 0), 0);
        return acc;
    }, {});
    return { medios, rows, totales, total: rows.reduce((total, r) => total + r.total, 0) };
}

/**
 * Lo que se entregó sin que entrara plata: descuentos, saldo a favor, gift cards aplicadas y los
 * tickets de los medios que no son plata. Para el control importa tanto como lo cobrado: es donde
 * se esconde un "regalo" que nadie anotó.
 */
export function sinPlata(tickets) {
    const vigentes = tickets.filter((t) => !t.cancelled);
    const suma = (lista, f) => lista.reduce((total, t) => total + Number(f(t) || 0), 0);
    const porMedio = (m) => vigentes.filter((t) => t.paymentType === m);
    const conDescuento = vigentes.filter((t) => t.discount_amount > 0);
    const codigos = {};
    conDescuento.forEach((t) => {
        const clave = t.discount_code || 'Descuento en la línea';
        const c = codigos[clave] || (codigos[clave] = { code: clave, cantidad: 0, monto: 0 });
        c.cantidad += 1;
        c.monto += t.discount_amount;
    });
    return {
        descuentos: suma(conDescuento, (t) => t.discount_amount),
        descuentosCantidad: conDescuento.length,
        codigos: Object.values(codigos).sort((a, b) => b.monto - a.monto),
        saldoAFavor: suma(vigentes, (t) => t.store_credit_applied),
        giftCards: suma(vigentes, (t) => t.gift_card_applied),
        cuentaEquipo: suma(porMedio(6), (t) => t.total),
        incluidos: porMedio(7).length,
        premios: porMedio(8).length,
    };
}

/**
 * Cuántas transferencias tienen comprobante: las tildadas en el cierre (o el conteo a mano, en las
 * cajas viejas) más las que un admin encontró después en el banco. `null` si no se declaró nada.
 */
export function transferenciasConComprobante(linea) {
    const declaradas = linea.ticked_at_close !== null && linea.ticked_at_close !== undefined
        ? linea.ticked_at_close
        : linea.count_declared;
    if (declaradas === null || declaradas === undefined) return null;
    return declaradas + Number(linea.verified_by_admin || 0);
}

/**
 * En qué quedó una línea de la conciliación, para pintar su estado:
 *   - 'conciliada' / 'con_diferencia': un admin ya la marcó (gana sobre todo lo demás);
 *   - 'descuadra': lo declarado no coincide con el sistema y nadie la miró;
 *   - 'cuadra': coincide;
 *   - 'informativa': no hay nada declarado contra qué comparar.
 */
export function estadoLinea(linea) {
    if (linea.check && linea.check.status === 'ok') return 'conciliada';
    if (linea.check && linea.check.status === 'diferencia') return 'con_diferencia';
    if (linea.key === 'transferencias') {
        const conComprobante = transferenciasConComprobante(linea);
        if (conComprobante === null) return 'informativa';
        return conComprobante === linea.count_system ? 'cuadra' : 'descuadra';
    }
    if (linea.key === 'mercadopago') return hayDiferencia(linea.difference) ? 'descuadra' : 'cuadra';
    if (linea.declared === null || linea.declared === undefined) return 'informativa';
    return hayDiferencia(linea.difference) ? 'descuadra' : 'cuadra';
}

/** Cuántas líneas quedan por mirar: las que descuadran y ningún admin marcó. */
export function pendientesDeConciliar(lineas = [], transferencias = []) {
    const lineasPendientes = lineas.filter((l) => estadoLinea(l) === 'descuadra').length;
    const transfPendientes = transferencias.filter((t) => t.check && t.check.checked_at_close === false && !t.check.status).length;
    return lineasPendientes + transfPendientes;
}
